import {
    Box,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TextField,
    Typography
} from '@mui/material'
import GenericTabs from '../genericTabs/genericTabs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import EmptyView from '../postionsWidget/EmptyView'
import { syncOrders } from '../../services/apis/orders'
import { useDispatch, useSelector } from 'react-redux'
import { Order } from '../../proto/generated/order'
import OrderIcon from '../../assets/icons/Order'
import { Broker, TradingAccountStatus, TradingAccountSyncRequest, TradingAccountSyncRequest_Type } from '../../proto/generated/trading-bridge'
import { selectBrokers, selectPrimaryTradingAccount } from '../../selectors/user'
import { selectOrdersHistory } from '../../selectors/orders'
import { openConnectAccountDialog } from '../../reducer/orders.reducer'
import SearchIcon from '../../assets/icons/SearchIcon'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import OrdersTableHeader from './OrdersTableHeader'
import OrderRow from './OrderRow'
import OrderBatchGroupRow from './OrderBatchGroupRow'
import { BrokerId } from '../../util/tradingAccounts'
import RefreshIcon from '../../assets/icons/RefreshIcon'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import ReconnectView from '../common/ReconnectView'
import ReconnectTradingAccount from '../common/ReconnectTradingAccount'
import ClearIcon from '../../assets/icons/ClearIcon'
import TradingAccountSelector from '../tradingAccountStatus/TradingAccountSelector'
import { groupIcebergBasketOrders, isIcebergGroup } from '../../util/icebergOrders'
import { filterOrdersForOrdersWidgetTab, type OrdersWidgetTableView } from '../../util/orders'
import { OrdersNavButtonsDetailed } from '../postionsWidget'

export const navItems = [
    {
        key: OrdersNavButtonsDetailed.OPEN,
        label: 'Open'
    },
    {
        key: OrdersNavButtonsDetailed.COMPLETE,
        label: 'Complete'
    },
    {
        key: OrdersNavButtonsDetailed.REJECTED,
        label: 'Rejected'
    },
    {
        key: OrdersNavButtonsDetailed.CANCELLED,
        label: 'Cancelled'
    },
    {
        key: OrdersNavButtonsDetailed.ALL,
        label: 'All'
    },
]
const OrdersWidget = () => {
    const [showContent, setShowContent] = useState(false)
    const [tableViewType, setTableViewType] = useState<OrdersNavButtonsDetailed>(OrdersNavButtonsDetailed.OPEN)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const brokers: Broker[] = useSelector(selectBrokers)
    const ordersData = useSelector(selectOrdersHistory)
    const [filterOrdersData, setFilterOrdersData] = useState<Order[]>([])
    const [searchText, setSearchText] = useState('')
    const dispatch = useDispatch()
    const [showSearch, setShowSearch] = useState(false)
    const [showReconnect, setShowReconnect] = useState(false);
    const getFilteredOrders = (orders: Order[]) => {
        if (showSearch && searchText.trim().length > 0) {
            return orders;
        }
        return filterOrdersForOrdersWidgetTab(orders, tableViewType as OrdersWidgetTableView);
    };
    const visibleOrders = getFilteredOrders(filterOrdersData);
    const orderDataSecKeys = useMemo(() => {
        const data: string[] = []
        ordersData?.forEach((order) => {
            const secKey = getSecurityKey(order.security?.exchange ?? 0, order.security?.code ?? '')
            data.push(secKey)
        })
        return data
    }, [ordersData])

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (primaryTradingAccount) {
            setShowReconnect(false);
            setShowContent(true);
        } else {
            setShowContent(false);
            timer = setTimeout(() => {
                setShowReconnect(true);
            }, 200);
        }
        return () => {
            clearTimeout(timer);
        };
    }, [primaryTradingAccount]);

    useEffect(() => {
        let cancelled = false
        const run = async () => {
            if (!ordersData?.length) {
                setFilterOrdersData([])
                return
            }
            const results = await Promise.all(
                ordersData.map((data, i) =>
                    getSecurityAsync(orderDataSecKeys[i]).then((security) =>
                        (security?.symbol)?.toLowerCase()?.includes(searchText.trim().toLowerCase())
                    )
                )
            )
            if (!cancelled) {
                setFilterOrdersData(ordersData.filter((_, i) => results[i]))
            }
        }
        run()
        return () => { cancelled = true }
    }, [orderDataSecKeys, ordersData, searchText])

    const handleOnSync = useCallback(async () => {
        if (primaryTradingAccount && primaryTradingAccount.status !== TradingAccountStatus.ACTIVE) {
            dispatch(openConnectAccountDialog({
                type: 'reconnect',
                tradingAccountId: primaryTradingAccount.accountId
            }))
        } else {
            if (primaryTradingAccount) {
                const body: TradingAccountSyncRequest = {
                    tradingAccountId: primaryTradingAccount.accountId,
                    types: [TradingAccountSyncRequest_Type.ORDER_SYNC],
                    force: true
                }
                syncOrders({ body }).then((response) => {
                    // const orders = response.reverse()
                    // dispatch(setOrderHistory({ updates: orders }))
                    // setFilterOrdersData(orders)
                    setSearchText('')
                }).catch((error) => {
                    console.error('Something went wrong in syncing orders')
                })
            }
        }
    }, [dispatch, primaryTradingAccount])

    return (
        <Paper sx={{ display: 'flex', height: '100%', flexDirection: 'column', borderRadius: '6px' }}>
            <Grid container>
                <Grid container px={'8px'} py={0}
                    justifyContent={'space-between'}
                    alignItems={'center'}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <OrderIcon sx={{ color: (theme) => theme.palette.text.secondary }} />
                        <Typography variant={'body1'}>Orders</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {
                            brokers.find((broker) => broker.code === (primaryTradingAccount?.brokerCode??BrokerId.BROKER_UNKNOWN))?.syncAvailable &&
                            <IconButton onClick={handleOnSync} sx={{ height: '40px'}}>
                                <RefreshIcon />
                            </IconButton>
                        }
                        <TradingAccountSelector compact />
                    </Box>
                </Grid>
                <Divider style={{ width: '100%' }} />
            </Grid>
            {
                showContent  ?
                    (<>
                        <Grid container justifyContent={'space-between'} style={{ padding: '10px' }}>
                            <Grid item>
                                {!showSearch && ordersData?.length > 0 && (
                                    <GenericTabs<OrdersNavButtonsDetailed>
                                        items={navItems}
                                        onChange={(val) => {
                                            AnalyticsManager.logEvent(`${val === OrdersNavButtonsDetailed.OPEN ? 'Open' : 'All'} Tab Clicked`, { source: 'Orders' })
                                            if (val) setTableViewType(val)
                                        }}
                                        value={tableViewType}
                                        tabProps={{ sx: { minWidth: 60 } }}
                                        sx={{ height: 32, pr: 1 }}
                                    />)}
                            </Grid>
                            {!!(ordersData && ordersData?.length > 0) && (

                                (!showSearch ? (
                                    <Grid item >
                                        <IconButton onClick={() => setShowSearch(true)} size="small">
                                            <SearchIcon />
                                        </IconButton>
                                    </Grid>
                                ) : (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            autoFocus
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            placeholder="Search Symbol"
                                            InputProps={{
                                                sx: { height: 32, pl: 1 },
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSearchText('');
                                                                setShowSearch(false);
                                                            }}
                                                        >
                                                            <ClearIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                )))
                            }
                        </Grid>
                        {
                            (visibleOrders.length == 0) ?
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    padding: 2,
                                    justifyContent: 'center'
                                }}>
                                    <EmptyView
                                        text={tableViewType === OrdersNavButtonsDetailed.OPEN ? 'No open orders' : 'No orders. Place an order to view order history.'}
                                    />
                                </div> :
                                <TableContainer sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflowX: 'auto' }}>
                                    {
                                        ordersData?.length === 0 ?
                                            <Box sx={{ padding: 2 }}>
                                                <EmptyView text="No orders. Place an order to view order history." />
                                            </Box>
                                            :
                                            <Table stickyHeader sx={{
                                                tableLayout: 'fixed'
                                            }} className={'font-feature-num'}>
                                                <OrdersTableHeader tabType={tableViewType} />
                                                <TableBody sx={{ overflow: 'auto' }}>
                                                    {groupIcebergBasketOrders([...visibleOrders])
                                                        .sort((a, b) => {
                                                            const aOrder = isIcebergGroup(a) ? a.allOrders[0] : a;
                                                            const bOrder = isIcebergGroup(b) ? b.allOrders[0] : b;
                                                            return (bOrder?.created ?? 0) - (aOrder?.created ?? 0);
                                                        })
                                                        .map(orderOrGroup => {
                                                            const key = isIcebergGroup(orderOrGroup)
                                                                ? `iceberg-${orderOrGroup.batchId}-${orderOrGroup.basketId}`
                                                                : (orderOrGroup.orderId || orderOrGroup.clientOrderId);
                                                            if (isIcebergGroup(orderOrGroup) && orderOrGroup.isBasketGroup) {
                                                                return <OrderBatchGroupRow key={key} batchGroup={orderOrGroup} tabType={tableViewType} />;
                                                            }
                                                            return <OrderRow key={key} order={orderOrGroup} tabType={tableViewType} />;
                                                        })
                                                    }
                                                </TableBody>
                                            </Table>
                                    }
                                </TableContainer>
                        }
                    </>) : (
                        showReconnect && (<>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Stack justifyContent={'center'} alignItems={'center'}>
                                    <ReconnectView text={primaryTradingAccount ? "Reconnect trading account to view orders." : "Connect trading account to view orders."} />
                                    <ReconnectTradingAccount primaryTradingAccount={primaryTradingAccount} />
                                </Stack>
                            </Box>
                        </>)
                    )
            }
        </Paper>
    )
}

export default OrdersWidget